import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { OrbitProgress } from "react-loading-indicators";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { UseShopControllersData } from "../../../services/API/ShopAPI/GetShopControllers.jsx";
import { UseDeviceSettings } from "../../../services/API/ControllerAPI/GetDevicesSettings.jsx";
import { useScheduledSettingsTrigger } from "../../../services/API/ControllerAPI/GetUpdatedSettings.jsx";
import { useSettingRangeRefresh } from "../../../services/API/ControllerAPI/GetSettingsRange.jsx";

import { ChevronDown, ChevronRight, Crown } from "lucide-react";

import {
  SETTINGS_TYPE_MAP,
  HARDWARE_TYPE_MAP,
  HARDWARE_DOOR_COUNT_MAP,
  INTRUSION_INPUT_MAP,
  INDEFINITE_SETTINGS_CONFIG,
} from "../../constants.js";

const EMPTY_SETTINGS = [];

// Build the table settings in the desired display order.
const TABLE_SETTINGS_TYPE_MAP = [
  SETTINGS_TYPE_MAP.find((s) => s.name === "Opening Time"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "Watchmaker Mode"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "Validation Mode"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "Open Door Warning"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "Security Mode"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "One Door Only"),

  {
    // Frontend-only grouped setting.
    name: "Enhanced Sound Set",
    isEnhancedSound: true,
    addr: "enhanced-sound",
    bit_pos: "group",
  },

  SETTINGS_TYPE_MAP.find((s) => s.name === "Intrusion Alarm"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "2-Way Authentication"),
  SETTINGS_TYPE_MAP.find((s) => s.name === "Opening Delay"),
].filter(Boolean);

/**
 * Maps selected settings to table header icons.
 */
const settingIcons = {
  "Security Mode": <Crown size={15} className="text-stone-700" />,
  "One Door Only": <Crown size={15} className="text-stone-700" />,
  "Enhanced Sound Set": <Crown size={15} className="text-stone-700" />,
  "Intrusion Alarm": <Crown size={15} className="text-stone-700" />,
  "2-Way Authentication": <Crown size={15} className="text-stone-700" />,
  "Opening Delay": <Crown size={15} className="text-stone-700" />,
};

/**
 * Settings that should show output details for multi-output controllers.
 */
const outputBasedSettingNames = [
  "Opening Time",
  "Watchmaker Mode",
  "Opening Delay",
  "2-Way Authentication",
];

// Expire data
const settingsWithExpiration = [
  "Security Mode",
  "One Door Only",
  "Enhanced Sound Set",
  "Intrusion Alarm",
  "2-Way Authentication",
  "Opening Delay",
];

/**
 * Settings that should show input details only for DEM controllers.
 */
const inputBasedSettingNames = ["Intrusion Alarm"];

/**
 * Returns the hardware label based on the hardware version.
 */
const getHardwareLabel = (hardware_v) =>
  HARDWARE_TYPE_MAP?.[Number(hardware_v)] ??
  (hardware_v != null ? `Unknown (${hardware_v})` : "-");

/**
 * Returns the safest available device name.
 */
const getSafeDeviceName = (controller) => {
  return (
    controller?.device_info?.ssid ??
    controller?.ssid ??
    controller?.name ??
    "Device name not updated"
  );
};

/**
 * Returns the safest available firmware version.
 */
const getSafeFirmwareVersion = (controller) => {
  return (
    controller?.device_info?.firmware_v ??
    controller?.device_info?.firmwareVersion ??
    controller?.firmware_v ??
    controller?.firmwareVersion ??
    null
  );
};

/**
 * Checks if a setting should display multiple outputs.
 */
const isOutputBasedSetting = (settingType) => {
  return outputBasedSettingNames.includes(settingType.name);
};

/**
 * Checks if a setting should display multiple inputs.
 */
const isInputBasedSetting = (settingType) => {
  return inputBasedSettingNames.includes(settingType.name);
};

/**
 * Returns the number of outputs for each supported hardware type.
 */
const getOutputCountForController = (controller) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  if (hardwareValue === 18 || hardwareValue === 23)
    return 4; // Air2Four has 4 outputs.
  else if (hardwareValue === 2 || hardwareValue === 3)
    return 2; // DEM has 8 outputs for output-based settings.
  else return 1;
};

/**
 * Returns the number of inputs for DEM controllers.
 */
const getInputCountForController = (controller) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  // Use the centralized hardware-to-door mapping from constants.
  return HARDWARE_DOOR_COUNT_MAP[hardwareValue] ?? 1;
};

/**
 * Checks whether a controller and setting should use expandable output rows.
 */
const shouldUseExpandableOutputs = (controller, settingType) => {
  const outputCount = getOutputCountForController(controller);

  // Air2Mini uses the same expandable output view even with a single output.
  if (isAir2MiniController(controller)) {
    return outputCount >= 1 && isOutputBasedSetting(settingType);
  }

  // Opening Delay should remain expandable even on single-output controllers.
  if (settingType.name === "Opening Delay") {
    return outputCount >= 1;
  }

  return outputCount > 1 && isOutputBasedSetting(settingType);
};

/**
 * Checks whether the controller is a DEM hardware type.
 */
const isDemController = (controller) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  const hardwareLabel = HARDWARE_TYPE_MAP[hardwareValue] ?? "";

  return hardwareLabel.startsWith("DEM");
};

/**
 * Checks whether the controller is an Air2Mini hardware type.
 */
const isAir2MiniController = (controller) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  const hardwareLabel = HARDWARE_TYPE_MAP[hardwareValue] ?? "";

  // Includes all Air2Mini hardware variants.
  return hardwareLabel.startsWith("Air2Mini");
};

/**
 * Checks whether the controller is an Air2One hardware type.
 */
const isAir2OneController = (controller) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  const hardwareLabel = HARDWARE_TYPE_MAP[hardwareValue] ?? "";

  return hardwareLabel === "Air2One";
};

const shouldUseExpandableInputs = (controller, settingType) => {
  // Only DEM controllers use input-based Intrusion Alarm expansion.
  return (
    isDemController(controller) &&
    getInputCountForController(controller) > 0 &&
    isInputBasedSetting(settingType)
  );
};

/**
 * Checks whether Air2Four Intrusion Alarm should use output expansion.
 */
const shouldUseIntrusionAlarmOutputs = (controller, settingType) => {
  const hardwareValue = Number(
    controller?.device_info?.hardware_v ?? controller?.hardware_v,
  );

  const hardwareLabel = HARDWARE_TYPE_MAP[hardwareValue] ?? "";

  // Air2Four controllers use output-based Intrusion Alarm handling.
  const isAir2Four =
    hardwareLabel === "Air2Four" || hardwareLabel === "Air2Four (LAN)";

  return isAir2Four && settingType.name === "Intrusion Alarm";
};

/**
 * Checks whether Air2Mini or Air2One Intrusion Alarm should use a single-output dropdown.
 */
const shouldUseSingleIntrusionAlarmOutput = (controller, settingType) => {
  return (
    settingType.name === "Intrusion Alarm" &&
    (isAir2MiniController(controller) || isAir2OneController(controller))
  );
};

/**
 * Builds a unique key for each setting using address and bit position.
 */
const settingKey = (addr, bit_pos) => `${Number(addr)}:${Number(bit_pos)}`;

/**
 * Checks whether a settings list contains too many invalid V values.
 * The device is considered invalid when more than 10 settings have a negative V value.
 */
const hasInvalidSettingValue = (settingsList) => {
  if (!Array.isArray(settingsList)) {
    return false;
  }

  // Count how many settings have a negative V value.
  const invalidSettingsCount = settingsList.filter((setting) => {
    const value = Number(setting?.V);

    return Number.isFinite(value) && value < 0;
  }).length;

  // Show the device error only when more than 10 settings are invalid.
  return invalidSettingsCount > 10;
};

/**
 * Returns the API address and bit position for a specific door.
 */
const getDoorSettingAddress = (settingType, doorNumber) => {
  const doorIndex = doorNumber - 1;

  switch (settingType.name) {
    case "Opening Time":
      return {
        addr: 72 + doorIndex,
        bit_pos: 0,
      };

    case "Watchmaker Mode":
      return {
        addr: 122,
        bit_pos: 1 << doorIndex,
      };

    case "2-Way Authentication":
      return {
        addr: 104 + doorIndex,
        bit_pos: 0,
      };

    case "Opening Delay":
      return {
        addr: 88 + doorIndex,
        bit_pos: 0,
      };

    default:
      return {
        addr: settingType.addr,
        bit_pos: settingType.bit_pos,
      };
  }
};

/**
 * Converts raw API values into ON/OFF display values.
 */
const getOnOffDisplay = (value) => {
  const isOn = value > 0 || value === true;
  const isOff = value === 0 || value === "0" || value === false;

  if (isOn) return { label: "ON", state: "on" };
  if (isOff) return { label: "OFF", state: "off" };

  return { label: "-", state: "na" };
};

/**
 * Converts inverted boolean values into ON/OFF display values.
 *
 * 0 = ON
 * 1 = OFF
 */
const getInvertedOnOffDisplay = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return { label: "-", state: "na" };
  }

  if (numericValue === 0) {
    return { label: "ON", state: "on" };
  }

  if (numericValue === 1) {
    return { label: "OFF", state: "off" };
  }

  return { label: "-", state: "na" };
};

/**
 * Converts numeric values into OFF, LOCAL, or NETWORK.
 *
 * 0 = OFF
 * 1 = LOCAL
 * 2 = NETWORK
 */
const getConnectionModeDisplay = (setting, timeoutSetting = null) => {
  // Prefer the raw V field when available, otherwise use new_val.
  const value = setting?.V ?? setting?.new_val;
  const numericValue = Number(value);

  const settingName = setting?.name;

  if (!Number.isFinite(numericValue)) {
    return {
      label: "-",
      state: "na",
    };
  }

  if (numericValue === 0) {
    return {
      label: "OFF",
      state: "off",
    };
  }

  //Handling security mode and one door only
  if (settingName === "Security Mode" || settingName === "One Door Only") {
    if (numericValue === 1) {
      return {
        label: "ON",
        state: "on",
        connectionMode: "LOCAL",
      };
    }

    if (numericValue === 2) {
      return {
        label: "ON",
        state: "on",
        connectionMode: "NETWORK",
      };
    }
  } else if (settingName === "Validation Mode") {
    return {
      label: "ON",
      state: "on",
      connectionMode: numericValue + " Hours",
    };
  } else if (settingName === "Open Door Warning") {
    // Convert the timeout setting into seconds.
    const rawTimeout = Number(timeoutSetting?.new_val ?? timeoutSetting?.value);

    // The timeout value is stored in multiples of 5 seconds.
    const timeoutSeconds = Number.isFinite(rawTimeout) ? rawTimeout * 5 : null;

    return {
      label: "ON",
      state: "on",
      connectionMode: timeoutSeconds !== null ? `${timeoutSeconds}s` : "-",
    };
  }

  return {
    label: "-",
    state: "na",
  };
};

/**
 * Converts the controller volume into a safe level from 0 to 4.
 */
const normalizeVolumeLevel = (value) => {
  const volumeLevel = Number(value);

  if (!Number.isFinite(volumeLevel)) return 0;

  return Math.max(0, Math.min(4, Math.round(volumeLevel)));
};

/**
 * Converts the remaining hours into an expiration date.
 *
 * While the setting is active, rem_hours represents the amount of time
 * remaining until expiration and is used to calculate the expiration date.
 */
const getExpiryDateFromRemainingHours = (remHours) => {
  const hours = Number(remHours);

  // Return null when rem_hours is invalid.
  if (!Number.isFinite(hours)) {
    return null;
  }

  // Calculate the expiration date from the current time
  // using the remaining hours reported by the controller.
  const expiryDate = new Date();
  expiryDate.setTime(expiryDate.getTime() + hours * 60 * 60 * 1000);

  // Return the expiration date using day/month/year format.
  return expiryDate.toLocaleDateString("en-GB");
};

// // Checks whether a DD/MM/YYYY expiration date is already in the past.
// const isExpiryDateExpired = (expiryDateText) => {
//   if (!expiryDateText) {
//     return false;
//   }

//   const [day, month, year] = String(expiryDateText).split("/").map(Number);

//   if (!day || !month || !year) {
//     return false;
//   }

//   const expiryDate = new Date(year, month - 1, day);
//   expiryDate.setHours(23, 59, 59, 999);

//   return expiryDate.getTime() < Date.now();
// };

/**
 * Displays a single setting as a badge.
 */
const SettingBadge = ({
  value,
  showExpiration = false,
  hasIndefiniteSettings = false,
}) => {
  if (!value) return "-";

  // Define the expiration label based on the remaining time.
  const expirationLabel = value?.isExpired ? "Expired" : "Expires in";

  // Define the expiration badge color based on the expiration status.
  const expirationClassName = value?.isExpired
    ? "border-red-200 bg-red-100 text-red-700"
    : "border-green-200 bg-green-100 text-stone-500";

  // Display time-based settings (Opening Time / Opening Delay).
  if (value.state === "time") {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        {/* Show the configured time */}
        <span className="inline-flex items-center justify-center rounded-md border border-stone-200 bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-700 whitespace-nowrap">
          {value.label}
        </span>

        {/* Show expiration status and date when available */}
        {showExpiration && (hasIndefiniteSettings || value?.expiryDateText) && (
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${expirationClassName}`}
          >
            {hasIndefiniteSettings
              ? "Indefinite"
              : `${expirationLabel} (${value.expiryDateText})`}
          </span>
        )}
      </div>
    );
  }

  if (value.connectionMode) {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        <div className="inline-flex items-center gap-1">
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
            ON
          </span>

          <span
            className={`inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold border ${
              value.connectionMode === "LOCAL"
                ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                : value.connectionMode === "NETWORK"
                  ? "bg-blue-100 text-blue-700 border-blue-200"
                  : "bg-green-100 text-green-700 border-green-200"
            }`}
          >
            {value.connectionMode}
          </span>
        </div>

        {/* Show expiration status and date when available */}
        {showExpiration && (hasIndefiniteSettings || value?.expiryDateText) && (
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${expirationClassName}`}
          >
            {hasIndefiniteSettings
              ? "Indefinite"
              : `${expirationLabel} (${value.expiryDateText})`}
          </span>
        )}
      </div>
    );
  }

  // Display ON/OFF and expiration on separate lines.
  if (value.state === "on") {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
          {value.hideLabel ? null : value.label}
        </span>

        {/* Show expiration status and date when available */}
        {showExpiration && (hasIndefiniteSettings || value?.expiryDateText) && (
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${expirationClassName}`}
          >
            {hasIndefiniteSettings
              ? "Indefinite"
              : `${expirationLabel} (${value.expiryDateText})`}
          </span>
        )}
      </div>
    );
  }

  if (value.state === "local" || value.state === "network") {
    return (
      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200 whitespace-nowrap">
        {value.label}
      </span>
    );
  }

  if (value.state === "off") {
    return (
      <div className="inline-flex flex-col items-start gap-1">
        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-red-100 text-red-700 border border-red-200 whitespace-nowrap">
          {value.label}
        </span>

        {/* Show expired status when the setting has expired. */}
        {showExpiration && value?.isExpired && value?.expiryDateText && (
          <span className="inline-flex items-center rounded-md border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 whitespace-nowrap">
            Expired ({value.expiryDateText})
          </span>
        )}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-semibold bg-stone-100 text-stone-600 border border-stone-200 whitespace-nowrap">
      -
    </span>
  );
};

/**
 * Displays the Enhanced Sound Set details inside an expandable cell.
 */
const EnhancedSoundSetCell = ({
  value,
  expanded,
  onToggle,
  hasIndefiniteSettings = false,
}) => {
  // Get the controller volume level (0-4).
  const volumeLevel = normalizeVolumeLevel(value?.volume);

  // Convert the controller volume level (0-4) into a percentage.
  const volumePercent = (volumeLevel / 4) * 100;

  // Check whether the Enhanced Sound Set has any non-default value.
  const hasCustomSoundSetting =
    value?.memoSound?.state === "on" ||
    value?.softDoorWarning?.state === "on" ||
    volumePercent < 100;

  return (
    <div className="min-w-[180px] flex flex-col items-start">
      <div className="flex flex-row items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-stone-200 bg-stone-50 hover:bg-stone-100 text-xs font-semibold text-stone-700"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span>Sound Set</span>
        </button>

        <SettingBadge
          value={
            hasCustomSoundSetting
              ? { label: "ON", state: "on" }
              : { label: "OFF", state: "off" }
          }
          showExpiration={false}
        />
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center justify-between gap-2 rounded-md border border-stone-100 bg-white px-2 py-1">
            <span className="text-xs font-semibold text-stone-600">
              Memo Sound
            </span>
            <SettingBadge value={value?.memoSound} />
          </div>

          <div className="flex items-center justify-between gap-2 rounded-md border border-stone-100 bg-white px-2 py-1">
            <span className="text-xs font-semibold text-stone-600">
              Soft Door Open Warning
            </span>
            <SettingBadge value={value?.softDoorWarning} />
          </div>

          <div className="rounded-md border border-stone-100 bg-white px-2 py-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-stone-600">
                Volume
              </span>
              <span className="text-xs font-bold text-stone-800">
                {volumePercent}%
              </span>
            </div>

            <div className="h-2 w-full rounded-full bg-stone-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-stone-800"
                // Display the progress bar using the calculated percentage.
                style={{ width: `${volumePercent}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Show the expiration status for the Enhanced Sound Set. */}
      {hasCustomSoundSetting &&
        (hasIndefiniteSettings || value?.expiryDateText) && (
          <span
            className={`mt-2 inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${
              value?.isExpired
                ? "border-red-200 bg-red-100 text-red-700"
                : "border-green-200 bg-green-100 text-stone-500"
            }`}
          >
            {hasIndefiniteSettings ? (
              "Indefinite"
            ) : (
              <>
                {value?.isExpired ? "Expired" : "Expires in"} (
                {value.expiryDateText})
              </>
            )}
          </span>
        )}
    </div>
  );
};

/**
 * Displays multiple outputs or inputs inside an expandable table cell.
 */
const ExpandablePointSettingCell = ({
  controller,
  settingType,
  expanded,
  onToggle,
  getPointSettingDisplay,
  pointType = "Input",
  pointCount,

  // Allows the summary badge to use a value independent from the individual points.
  summaryValue = null,
  hasIndefiniteSettings = false,
}) => {
  const isInputCell = pointType === "Input";

  // Check whether the main Intrusion Alarm is disabled.
  const isIntrusionAlarmDisabled =
    settingType.name === "Intrusion Alarm" && summaryValue?.state === "off";

  // Inputs are read from device_info.inp_sts.
  // Outputs continue using the settings API.
  const pointValues = Array.from({ length: pointCount }, (_, index) => {
    const pointNumber = index + 1;

    return getPointSettingDisplay(controller, settingType, pointNumber);
  });

  const isTimeBasedSetting =
    settingType.name === "Opening Delay" || settingType.name === "Opening Time";

  const firstValue = pointValues[0];

  // Get the first ON value to preserve its expiration date.
  const onValue = pointValues.find((value) => value.state === "on");

  // Opening Delay is ON when at least one output has a value greater than 0.
  const hasOpeningDelay = pointValues.some((value) => {
    const seconds = Number(String(value?.label ?? "").replace("s", ""));
    return Number.isFinite(seconds) && seconds > 0;
  });

  // Opening Time is ON when at least one output differs from the default value.
  const hasCustomOpeningTime = pointValues.some((value) => {
    const seconds = Number(String(value?.label ?? "").replace("s", ""));
    return Number.isFinite(seconds) && seconds !== 5;
  });

  const mainValue =
    summaryValue ??
    (settingType.name === "Opening Delay"
      ? hasOpeningDelay
        ? {
            label: "ON",
            state: "on",
            expiryDateText:
              pointValues.find((value) => value?.expiryDateText)
                ?.expiryDateText ?? null,

            // Preserve the expiration status from the point value.
            isExpired:
              pointValues.find((value) => value?.expiryDateText)?.isExpired ??
              false,
          }
        : {
            label: "OFF",
            state: "off",
          }
      : isTimeBasedSetting
        ? firstValue
        : onValue
          ? {
              label: "ON",
              state: "on",
              expiryDateText: onValue?.expiryDateText ?? null,

              // Preserve the expiration status from the ON value.
              isExpired: onValue?.isExpired ?? false,

              hideLabel: false,
            }
          : pointValues.some((value) => value.state === "off")
            ? { label: "OFF", state: "off" }
            : { label: "-", state: "na" });

  // Check whether the summary badge should display the expiration date.
  const hasExpiration =
    settingsWithExpiration.includes(settingType.name) &&
    mainValue?.state === "on" &&
    (hasIndefiniteSettings || mainValue?.expiryDateText);

  return (
    <div className="min-w-[150px] items-center gap-2">
      <div className="flex flex-row items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border border-stone-200 bg-stone-50 hover:bg-stone-100 text-xs font-semibold text-stone-700"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}

          <span>
            {pointCount} {pointType}s
          </span>
        </button>

        {settingType.name === "Opening Time" && (
          <SettingBadge
            value={{ label: "ON", state: "on" }}
            showExpiration={false}
          />
        )}

        {/* Show the summary badge for Opening Delay and non-time-based settings. */}
        {(settingType.name === "Opening Delay" || !isTimeBasedSetting) &&
          (isInputCell || pointType === "Output") && (
            <SettingBadge value={mainValue} showExpiration={false} />
          )}
      </div>

      {expanded && (
        <div className="mt-2 flex flex-col gap-1">
          {pointValues.map((value, index) => {
            const pointNumber = index + 1;

            return (
              <div
                key={pointNumber}
                className="flex items-center justify-between gap-2 rounded-md border border-stone-100 bg-white px-2 py-1"
              >
                <span className="text-[12px] font-semibold text-stone-500">
                  {pointType} {pointNumber}
                </span>

                {/* Hide individual point states while the main Intrusion Alarm is disabled. */}
                <SettingBadge
                  value={
                    isIntrusionAlarmDisabled
                      ? { label: "-", state: "na" }
                      : value
                  }
                  showExpiration={false}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Show expiration status outside the button and expanded list. */}
      {hasExpiration &&
        (hasIndefiniteSettings || mainValue?.expiryDateText) && (
          <span
            className={`mt-2 inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${
              mainValue?.isExpired
                ? "border-red-200 bg-red-100 text-red-700"
                : "border-green-200 bg-green-100 text-stone-500"
            }`}
          >
            {hasIndefiniteSettings ? (
              "Indefinite"
            ) : (
              <>
                {mainValue?.isExpired ? "Expired" : "Expires in"} (
                {mainValue.expiryDateText})
              </>
            )}
          </span>
        )}
    </div>
  );
};

const SimpleDeviceSettigns = ({ isOpen, shop, onClose }) => {
  const shopId = shop?.id || "";

  // Get the indefinite settings configuration for the current shop.
  const indefiniteSettingsConfig = INDEFINITE_SETTINGS_CONFIG[shopId];

  // Check whether all controllers in the current shop use indefinite settings.
  const hasIndefiniteSettings = indefiniteSettingsConfig === "all";

  const hasRefreshed = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPointCells, setExpandedPointCells] = useState({});
  const [expandedEnhancedCells, setExpandedEnhancedCells] = useState({});

  const [manualRefreshing, setManualRefreshing] = useState(false);

  // Stores the selected controller status filter.
  const [statusFilter, setStatusFilter] = useState("all");

  // Stores the device IDs that failed during the last refresh.
  const [failedControllerIds, setFailedControllerIds] = useState([]);

  // Stores the error message for each failed device.
  const [failedControllerMessages, setFailedControllerMessages] = useState({});

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Var to check if settings are loaded on all controllers
  const [controllerSettingLoaded, setControllerSettingLoaded] = useState(false);

  /**
   * Fetches all controllers for the selected shop.
   */
  const {
    data: shopControllerData,
    isPending: shopControllerLoading,
    error: shopControllerDataError,
    refetch: refetchShopControllers,
  } = UseShopControllersData(shopId, {
    enabled: isOpen && !!shopId,

    // Do not automatically refetch when the browser window regains focus.
    refetchOnWindowFocus: false,

    // Do not automatically refetch when the network reconnects.
    refetchOnReconnect: false,

    // Do not automatically refetch cached data when the component mounts again.
    refetchOnMount: false,
  });

  /**
   * Normalizes the controllers API response into an array.
   */
  const controllers = useMemo(() => {
    return Array.isArray(shopControllerData?.controllerDetails)
      ? shopControllerData.controllerDetails
      : [];
  }, [shopControllerData?.controllerDetails]);

  /**
   * Extracts controller IDs used to fetch device settings.
   */
  const controllerIds = useMemo(() => {
    return controllers.map((controller) => controller.id).filter(Boolean);
  }, [controllers]);

  // Get the controllers that use indefinite settings in the current shop.
  const indefiniteControllerIds = useMemo(() => {
    if (indefiniteSettingsConfig === "all") {
      return controllerIds;
    }

    if (!Array.isArray(indefiniteSettingsConfig)) {
      return [];
    }

    return controllerIds.filter((controllerId) =>
      indefiniteSettingsConfig.includes(controllerId),
    );
  }, [controllerIds, indefiniteSettingsConfig]);

  // Get the controllers that continue using scheduled settings.
  const scheduledControllerIds = useMemo(() => {
    return controllerIds.filter(
      (controllerId) => !indefiniteControllerIds.includes(controllerId),
    );
  }, [controllerIds, indefiniteControllerIds]);

  // Check whether a specific controller uses indefinite settings.
  const controllerUsesIndefiniteSettings = useCallback(
    (controller) => {
      const controllerId = controller?.id ?? controller?.device_id;

      return indefiniteControllerIds.includes(controllerId);
    },
    [indefiniteControllerIds],
  );

  const shouldFetchScheduledSettings =
    isOpen && scheduledControllerIds.length > 0;

  const shouldFetchIndefiniteSettings =
    isOpen && indefiniteControllerIds.length > 0;

  const {
    data: scheduledControllerSettingData = EMPTY_SETTINGS,
    isPending: scheduledControllerSettingLoading,
    error: scheduledControllerSettingError,
    refetch: refetchScheduledControllerSettings,
  } = UseDeviceSettings(scheduledControllerIds, false, {
    enabled: shouldFetchScheduledSettings,
  });

  const {
    data: indefiniteControllerSettingData = EMPTY_SETTINGS,
    isPending: indefiniteControllerSettingLoading,
    error: indefiniteControllerSettingError,
    refetch: refetchIndefiniteControllerSettings,
  } = UseDeviceSettings(indefiniteControllerIds, true, {
    enabled: shouldFetchIndefiniteSettings,
  });

  // Combine scheduled and indefinite settings into one list for the UI.
  const controllerSettingData = useMemo(() => {
    return [
      ...scheduledControllerSettingData,
      ...indefiniteControllerSettingData,
    ];
  }, [scheduledControllerSettingData, indefiniteControllerSettingData]);

  const controllerSettingError =
    scheduledControllerSettingError || indefiniteControllerSettingError;

  const refreshControllerScheduleSettingsMutation =
    useScheduledSettingsTrigger();

  const refreshSettingRangeMutation = useSettingRangeRefresh();

  const wait = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds));

  // Function to handel refresh on button press
  const handleManualRefresh = async () => {
    setManualRefreshing(true);

    try {
      await refreshSettings();
    } finally {
      setManualRefreshing(false);
    }
  };

  /**
   * Requests the cloud to refresh all controller settings.
   */
  const refreshSettings = async () => {
    if (controllerIds.length === 0) return;

    // Show loader while the entire refresh process is running.
    setControllerSettingLoaded(false);

    try {
      const refreshResults = await Promise.all([
        ...(scheduledControllerIds.length > 0
          ? [
              refreshControllerScheduleSettingsMutation.mutateAsync({
                controllerIds: scheduledControllerIds,
              }),
            ]
          : []),

        ...(indefiniteControllerIds.length > 0
          ? [
              refreshSettingRangeMutation.mutateAsync({
                controllerIds: indefiniteControllerIds,
              }),
            ]
          : []),
      ]);

      const refreshResult = {
        details: refreshResults.flatMap((result) => result?.details ?? []),
      };

      // Extract the device IDs that failed during the refresh.
      const failedIds =
        refreshResult.details
          ?.filter((item) => item?.status === "error")
          .map((item) => item?.deviceId)
          .filter(Boolean) ?? [];

      // Store the failed device IDs for the table.
      setFailedControllerIds(failedIds);

      // Store the error message for each failed device.
      const failedMessages =
        refreshResult.details
          ?.filter((item) => item?.status === "error")
          .reduce((messages, item) => {
            if (item?.deviceId) {
              const errorMessage = item?.message ?? "Error";

              // Display a friendly message for device response timeouts.
              if (
                errorMessage.includes(
                  "Timed out waiting for the direct method response from the device.",
                )
              ) {
                messages[item.deviceId] =
                  "Timed out waiting for the direct method response from the device.";
              } else {
                messages[item.deviceId] = errorMessage;
              }
            }

            return messages;
          }, {}) ?? {};

      // Keep the failed device messages available for the tooltip.
      setFailedControllerMessages(failedMessages);

      // Refresh the frontend using the data already stored in the database.
      await Promise.all([
        ...(scheduledControllerIds.length > 0
          ? [refetchScheduledControllerSettings()]
          : []),

        ...(indefiniteControllerIds.length > 0
          ? [refetchIndefiniteControllerSettings()]
          : []),

        refetchShopControllers(),
      ]);

      setControllerSettingLoaded(true);
    } catch (error) {
      console.error("REFRESH ERROR:", error);
      // Important: don't leave the popup stuck loading forever.
      setControllerSettingLoaded(true);
    }
  };

  const togglePointCell = (controller, settingType, type) => {
    const deviceId = controller?.id ?? controller?.device_id;
    const key = `${deviceId}:${settingType.name}:${type}`;

    setExpandedPointCells((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  /**
   * Opens or closes one Enhanced Sound Set cell.
   */
  const toggleEnhancedCell = (controller) => {
    const deviceId = controller?.id ?? controller?.device_id;
    const key = `${deviceId}:Enhanced Sound Set`;

    setExpandedEnhancedCells((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  /**
   * Handles popup behavior:
   * - Refresh settings when popup opens
   * - Close popup with Escape
   * - Prevent page scrolling
   */
  useEffect(() => {
    if (!isOpen) {
      hasRefreshed.current = false;
      setControllerSettingLoaded(false);
      return;
    }

    // Refresh only once when popup opens.
    if (controllerIds.length > 0 && !hasRefreshed.current) {
      hasRefreshed.current = true;
      refreshSettings();
    }

    // Prevent background scrolling.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Close with Escape.
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, searchTerm, onClose, controllerIds]);

  /**
   * Groups device settings by device ID.
   */
  const settingsByDeviceId = useMemo(() => {
    const map = {};

    const devices = Array.isArray(controllerSettingData)
      ? controllerSettingData
      : [];

    for (const device of devices) {
      const deviceId = device.deviceId;
      if (!deviceId) continue;

      const scheduled = Array.isArray(device?.settings) ? device.settings : [];

      if (!map[deviceId]) {
        map[deviceId] = {
          latestByKey: {},
          allSettings: [],
        };
      }

      for (const setting of scheduled) {
        const key = settingKey(setting?.addr, setting?.bit_pos);

        map[deviceId].allSettings.push(setting);

        const previous = map[deviceId].latestByKey[key];
        const previousTime = Number(previous?.sys_time ?? 0);
        const currentTime = Number(setting?.sys_time ?? 0);

        if (!previous || currentTime >= previousTime) {
          map[deviceId].latestByKey[key] = setting;
        }
      }
    }

    return map;
  }, [controllerSettingData]);

  /**
   * Checks whether a controller has at least one invalid setting value.
   * A V value below zero means the device settings are not valid.
   */
  const controllerHasInvalidSettings = useCallback(
    (controller) => {
      const deviceId = controller?.id ?? controller?.device_id;

      if (!deviceId) {
        return false;
      }

      const deviceSettings = settingsByDeviceId?.[deviceId]?.allSettings ?? [];

      return hasInvalidSettingValue(deviceSettings);
    },
    [settingsByDeviceId],
  );

  /**
   * Gets Enhanced Sound Set values for one controller.
   */
  const getEnhancedSoundSetDisplay = useCallback(
    (controllerRow) => {
      const deviceId = controllerRow?.id ?? controllerRow?.device_id;

      const controllerHasIndefiniteSettings =
        controllerUsesIndefiniteSettings(controllerRow);

      if (!deviceId) {
        return {
          memoSound: { label: "-", state: "na" },
          softDoorWarning: { label: "-", state: "na" },
          volume: 0,
        };
      }

      // Get Memo Sounds using address 186 and bit position 32.
      const memoSoundSetting =
        settingsByDeviceId?.[deviceId]?.latestByKey?.[settingKey(186, 32)] ??
        null;

      // Get Soft Door Open Warning using address 186 and bit position 64.
      const softDoorWarningSetting =
        settingsByDeviceId?.[deviceId]?.latestByKey?.[settingKey(186, 64)] ??
        null;

      // Get Volume using address 189 and bit position 0.
      const volumeSetting =
        settingsByDeviceId?.[deviceId]?.latestByKey?.[settingKey(189, 0)] ??
        null;

      // Selects the expiration date used by the Enhanced Sound Set.
      const selectedRemainingHours = Number(
        softDoorWarningSetting?.rem_hours ??
          volumeSetting?.rem_hours ??
          memoSoundSetting?.rem_hours,
      );

      // Shops with indefinite settings do not use expiration information.
      const selectedExpiryDate = controllerHasIndefiniteSettings
        ? null
        : Number.isFinite(selectedRemainingHours) && selectedRemainingHours > 0
          ? getExpiryDateFromRemainingHours(selectedRemainingHours)
          : null;

      // Indefinite settings must never be treated as expired.
      const selectedIsExpired = controllerHasIndefiniteSettings
        ? false
        : Number.isFinite(selectedRemainingHours) &&
          selectedRemainingHours === 0;

      return {
        memoSound: getOnOffDisplay(
          memoSoundSetting?.new_val ?? memoSoundSetting?.value,
        ),

        // Constant Door Open Alarm uses inverted values.
        softDoorWarning: getInvertedOnOffDisplay(
          softDoorWarningSetting?.new_val ?? softDoorWarningSetting?.value,
        ),

        volume: volumeSetting?.new_val ?? volumeSetting?.value ?? 0,

        // Uses the selected expiration date for the Enhanced Sound Set.
        expiryDateText: selectedExpiryDate,

        // Uses the calculated expiration status for the Enhanced Sound Set.
        isExpired: selectedIsExpired,
      };
    },
    [settingsByDeviceId, controllerUsesIndefiniteSettings],
  );

  /**
   * Gets one regular setting display value for a controller.
   */
  const getSettingDisplayForController = useCallback(
    (controllerRow, addr, bit_pos, settingName) => {
      const deviceId = controllerRow?.id ?? controllerRow?.device_id;

      const controllerHasIndefiniteSettings =
        controllerUsesIndefiniteSettings(controllerRow);

      if (!deviceId) {
        return { label: "-", state: "na" };
      }

      const key = settingKey(addr, bit_pos);
      const setting = settingsByDeviceId?.[deviceId]?.latestByKey?.[key];

      if (!setting) {
        return { label: "-", state: "na" };
      }

      const value = setting?.new_val;
      const remainingHours = Number(setting?.rem_hours);

      // Shops with indefinite settings do not use expiration information.
      const expiryDateText = controllerHasIndefiniteSettings
        ? null
        : Number.isFinite(remainingHours) && remainingHours > 0
          ? getExpiryDateFromRemainingHours(remainingHours)
          : null;

      // Indefinite settings must never be treated as expired.
      const isExpired = controllerHasIndefiniteSettings
        ? false
        : Number.isFinite(remainingHours) && remainingHours === 0;

      // Expired settings must always be displayed as OFF.
      if (isExpired && settingsWithExpiration.includes(settingName)) {
        return {
          label: "OFF",
          state: "off",
          expiryDateText,
          isExpired: true,
        };
      }
      // Display Opening Delay as OFF when the configured delay is 0 seconds.
      if (settingName === "Opening Delay") {
        const numericValue = Number(value);

        if (numericValue === 0) {
          return {
            label: "OFF",
            state: "off",
            expiryDateText,
            isExpired,
          };
        }

        return {
          label: `${numericValue}s`,
          state: "time",
          expiryDateText,
          isExpired,
        };
      }

      // Opening Time always displays its configured value in seconds.
      if (settingName === "Opening Time") {
        return {
          label: `${value ?? 0}s`,
          state: "time",
          expiryDateText,
          isExpired,
        };
      }

      // Get the Open Door Warning timeout setting.
      const openDoorWarningTimeout =
        settingsByDeviceId?.[deviceId]?.latestByKey?.[settingKey(136, 0)];

      if (
        settingName === "Security Mode" ||
        settingName === "One Door Only" ||
        settingName === "Validation Mode" ||
        settingName === "Open Door Warning"
      ) {
        return {
          ...getConnectionModeDisplay(setting, openDoorWarningTimeout),
          expiryDateText,
          isExpired,
        };
      }

      // All remaining settings use the standard ON/OFF display.
      return {
        ...getOnOffDisplay(value),
        expiryDateText,
        isExpired,
      };
    },
    [settingsByDeviceId, controllerUsesIndefiniteSettings],
  );

  /**
   * Gets one input/output-specific setting display value.
   */
  const getPointSettingDisplay = useCallback(
    (controllerRow, settingType, pointNumber) => {
      const deviceId = controllerRow?.id ?? controllerRow?.device_id;

      const controllerHasIndefiniteSettings =
        controllerUsesIndefiniteSettings(controllerRow);

      if (!deviceId) {
        return { label: "-", state: "na" };
      }

      const deviceSettings = settingsByDeviceId?.[deviceId]?.allSettings ?? [];

      let targetAddress = {
        addr: settingType.addr,
        bit_pos: settingType.bit_pos,
      };

      // DEM and Air2Four controllers use the dedicated Intrusion Alarm point mapping.
      if (
        settingType.name === "Intrusion Alarm" &&
        (isDemController(controllerRow) ||
          shouldUseIntrusionAlarmOutputs(controllerRow, settingType))
      ) {
        const intrusionInputAddress = INTRUSION_INPUT_MAP[pointNumber];

        if (!intrusionInputAddress) {
          return {
            label: "-",
            state: "na",
          };
        }

        targetAddress = intrusionInputAddress;
      } else {
        // Air controllers use the regular Intrusion Alarm setting address.
        targetAddress = getDoorSettingAddress(settingType, pointNumber);
      }

      const matchingSettings = deviceSettings.filter((setting) => {
        const sameAddress =
          Number(setting?.addr) === Number(targetAddress.addr);

        const sameBitPosition =
          Number(setting?.bit_pos) === Number(targetAddress.bit_pos);

        return sameAddress && sameBitPosition;
      });

      if (matchingSettings.length === 0) {
        return {
          label: "-",
          state: "na",
        };
      }

      const latestSetting = matchingSettings.reduce((latest, current) => {
        const latestTime = Number(latest?.sys_time ?? 0);
        const currentTime = Number(current?.sys_time ?? 0);

        return currentTime >= latestTime ? current : latest;
      }, matchingSettings[0]);

      const value = latestSetting?.new_val;

      const remainingHours = Number(latestSetting?.rem_hours);

      // Shops with indefinite settings do not use expiration information.
      const expiryDateText = controllerHasIndefiniteSettings
        ? null
        : Number.isFinite(remainingHours) && remainingHours > 0
          ? getExpiryDateFromRemainingHours(remainingHours)
          : null;

      // Indefinite settings must never be treated as expired.
      const isExpired = controllerHasIndefiniteSettings
        ? false
        : Number.isFinite(remainingHours) && remainingHours === 0;

      // Display Opening Time and Opening Delay as seconds.
      if (
        settingType.name === "Opening Time" ||
        settingType.name === "Opening Delay"
      ) {
        return {
          label: `${value ?? 0}s`,
          state: "time",
          expiryDateText,
          isExpired,
        };
      }

      return {
        ...getOnOffDisplay(value),
        expiryDateText,
        isExpired,
      };
    },
    [
      settingsByDeviceId,
      getSettingDisplayForController,
      controllerUsesIndefiniteSettings,
    ],
  );

  /**
   * Filters controllers using the search text.
   */
  const controllerRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return controllers.filter((controller) => {
      const id = String(controller?.id ?? "");
      const deviceId = String(controller?.device_id ?? "");

      const deviceName = String(
        controller?.device_info?.ssid ??
          controller?.ssid ??
          controller?.name ??
          "",
      ).toLowerCase();

      const hardware = String(
        getHardwareLabel(
          controller?.device_info?.hardware_v ?? controller?.hardware_v,
        ),
      ).toLowerCase();

      // Check the controller status using the latest refresh result.
      const controllerId = controller?.id ?? controller?.device_id;
      const isOffline = failedControllerIds.includes(controllerId);

      // Apply the selected status filter.
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "online" && !isOffline) ||
        (statusFilter === "offline" && isOffline);

      // Apply the text search filter.
      const matchesSearch =
        !query ||
        id.toLowerCase().includes(query) ||
        deviceId.toLowerCase().includes(query) ||
        deviceName.includes(query) ||
        hardware.includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [controllers, searchTerm, statusFilter, failedControllerIds]);

  // Count offline controllers using the full controller list.
  const offlineControllerCount = controllers.filter((controller) => {
    const id = controller?.id ?? controller?.device_id;

    return failedControllerIds.includes(id);
  }).length;

  // Count online controllers using the full controller list.
  const onlineControllerCount = controllers.length - offlineControllerCount;

  // Build the summary using the grouped table settings.
  const settingsSummary = useMemo(() => {
    return TABLE_SETTINGS_TYPE_MAP.map((settingType) => {
      let onCount = 0;

      for (const controller of controllerRows) {
        if (settingType.isEnhancedSound) {
          const value = getEnhancedSoundSetDisplay(controller);

          if (value.memoSound.state === "on") onCount += 1;
          if (value.softDoorWarning.state === "on") onCount += 1;

          continue;
        }

        // Count Air2Four Intrusion Alarm outputs.
        if (shouldUseIntrusionAlarmOutputs(controller, settingType)) {
          const outputCount = getOutputCountForController(controller);

          for (
            let outputNumber = 1;
            outputNumber <= outputCount;
            outputNumber++
          ) {
            const value = getPointSettingDisplay(
              controller,
              settingType,
              outputNumber,
            );

            if (value.state === "on") onCount += 1;
          }

          continue;
        }

        if (shouldUseExpandableInputs(controller, settingType)) {
          const inputCount = getInputCountForController(controller);

          for (let inputNumber = 1; inputNumber <= inputCount; inputNumber++) {
            // Count active inputs using the current inp_sts bitmask.
            const value = getPointSettingDisplay(
              controller,
              settingType,
              inputNumber,
            );

            if (value.state === "on") {
              onCount += 1;
            }
          }

          continue;
        }

        if (shouldUseExpandableOutputs(controller, settingType)) {
          const outputCount = getOutputCountForController(controller);

          for (
            let outputNumber = 1;
            outputNumber <= outputCount;
            outputNumber++
          ) {
            const value = getPointSettingDisplay(
              controller,
              settingType,
              outputNumber,
            );

            if (value.state === "on") onCount += 1;
          }

          continue;
        }

        const value = getSettingDisplayForController(
          controller,
          settingType.addr,
          settingType.bit_pos,
          settingType.name,
        );

        if (value.state === "on") onCount += 1;
      }

      return {
        name: settingType.name,
        onCount,
      };
    });
  }, [
    controllerRows,
    getSettingDisplayForController,
    getPointSettingDisplay,
    getEnhancedSoundSetDisplay,
  ]);

  /**
   * Builds all table columns.
   */
  const settingsColumns = useMemo(() => {
    const baseColumns = [
      {
        header: "Device Name",
        id: "deviceName",
        accessorFn: (row) => getSafeDeviceName(row),
        cell: ({ row, getValue }) => {
          const ssid = getValue();
          const id = row.original?.id ?? row.original?.device_id ?? "-";

          // Check whether this controller failed during the last refresh.
          const hasRefreshError = failedControllerIds.includes(id);

          // Get the refresh error message for this controller.
          const refreshErrorMessage = failedControllerMessages[id] ?? "Error";

          // Check whether this device contains an invalid V value.
          const hasInvalidSettings = controllerHasInvalidSettings(row.original);

          return (
            <div className="min-w-[180px]">
              <div className="flex items-center gap-1">
                <div className="text-sm font-medium text-stone-800">{ssid}</div>
              </div>

              <div className="text-xs text-stone-500 break-all">{id}</div>

              {/* Show an error message when the device contains invalid settings. */}
              {hasInvalidSettings && (
                <div className="mt-1 text-xs font-semibold text-orange-700">
                  Device settings not valid
                </div>
              )}
            </div>
          );
        },
      },
      //Status Connected controller cosmos
      {
        header: "Status",
        id: "status",

        cell: ({ row }) => {
          const controller = row.original;

          const id = controller?.id ?? controller?.device_id;

          const lastUpdated = controller?.last_updated;

          // Convert the last update timestamp to date and time.
          const lastUpdatedDate = lastUpdated
            ? new Date(Number(lastUpdated)).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-";

          const hasRefreshError = failedControllerIds.includes(id);

          const status = hasRefreshError ? "Offline" : "Online";

          const rawErrorMessage = hasRefreshError
            ? (failedControllerMessages[id] ?? "Error")
            : null;

          const errorMessage = rawErrorMessage?.includes(
            "Timed out waiting for the direct method response from the device.",
          )
            ? "Device did not\nrespond in time"
            : rawErrorMessage;

          const statusClasses =
            status === "Online"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-slate-300 bg-slate-100 text-slate-600";

          return (
            <div className="flex flex-col items-start gap-1">
              <span
                className={`inline-flex w-fit rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClasses}`}
              >
                {status}
              </span>

              {hasRefreshError && (
                <span className="inline-flex w-fit min-w-[115px] rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 leading-tight line-clamp-2">
                  {errorMessage}
                </span>
              )}

              <span className="text-xs text-stone-500">
                Last Update: {lastUpdatedDate}
              </span>
            </div>
          );
        },
      },
      {
        header: "Firmware",
        id: "firmwareVersion",
        accessorFn: (row) => getSafeFirmwareVersion(row),
        cell: ({ row, getValue }) => {
          const firmwareVersion = getValue();

          return (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-stone-700">
                {firmwareVersion ?? "Not updated"}
              </span>
            </div>
          );
        },
      },
    ];

    // Build table columns using the grouped settings map.
    const settingColumns = TABLE_SETTINGS_TYPE_MAP.map((settingType) => ({
      header: () => (
        <div className="w-[150px]">
          <div className="flex items-center gap-1">
            {settingIcons[settingType.name]}

            <span className="leading-tight whitespace-normal">
              {settingType.name}
            </span>
          </div>
        </div>
      ),

      id: `setting_${settingType.addr}_${settingType.bit_pos}`,

      accessorFn: (row) => {
        if (settingType.isEnhancedSound) {
          return getEnhancedSoundSetDisplay(row);
        }

        return getSettingDisplayForController(
          row,
          settingType.addr,
          settingType.bit_pos,
          settingType.name,
        );
      },

      cell: ({ row, getValue }) => {
        // Only controllers configured for indefinite settings use indefinite mode.
        const controllerHasIndefiniteSettings =
          controllerUsesIndefiniteSettings(row.original);
        if (settingType.isEnhancedSound) {
          const deviceId = row.original?.id ?? row.original?.device_id;
          const expandKey = `${deviceId}:Enhanced Sound Set`;
          const expanded = !!expandedEnhancedCells[expandKey];

          return (
            <EnhancedSoundSetCell
              value={getValue()}
              expanded={expanded}
              onToggle={() => toggleEnhancedCell(row.original)}
              hasIndefiniteSettings={controllerHasIndefiniteSettings}
            />
          );
        }

        // Show input expansion for DEM controllers on Intrusion Alarm.
        if (shouldUseExpandableInputs(row.original, settingType)) {
          const deviceId = row.original?.id ?? row.original?.device_id;
          const expandKey = `${deviceId}:${settingType.name}:Input`;
          const expanded = !!expandedPointCells[expandKey];

          const deviceInput = getInputCountForController(row.original);

          // Handle DEM controllers with a single input.
          if (deviceInput === 1) {
            // Read the configured Intrusion Alarm state instead of the physical input state.
            const value = getSettingDisplayForController(
              row.original,
              settingType.addr,
              settingType.bit_pos,
              settingType.name,
            );

            // Stack the summary badge above the button when displaying
            // an expiration date or a time-based setting.
            const shouldStack =
              (settingsWithExpiration.includes(settingType.name) &&
                value?.state === "on" &&
                (hasIndefiniteSettings || value?.expiryDateText)) ||
              settingType.name === "Opening Delay" ||
              settingType.name === "Opening Time";

            return (
              <div className="flex flex-col gap-2">
                {/* Input and status row */}
                <div className="flex items-center gap-2">
                  {/* Input button */}
                  <div className="inline-flex items-center rounded-md border border-stone-200 bg-stone-50 px-3 py-1">
                    <span className="text-xs font-semibold text-stone-600">
                      Input 1
                    </span>
                  </div>

                  {/* ON / OFF badge */}
                  <SettingBadge
                    value={value}
                    showExpiration={false}
                    hasIndefiniteSettings={controllerHasIndefiniteSettings}
                  />
                </div>

                {/* Show the expiration status for the single input. */}
                {shouldStack &&
                  (hasIndefiniteSettings || value?.expiryDateText) && (
                    <span
                      className={`inline-flex self-end rounded-md border px-2 py-0.5 text-xs font-semibold ${
                        value?.isExpired
                          ? "border-red-200 bg-red-100 text-red-700"
                          : "border-green-200 bg-green-100 text-stone-500"
                      }`}
                    >
                      {hasIndefiniteSettings ? (
                        "Indefinite"
                      ) : (
                        <>
                          {value?.isExpired ? "Expired" : "Expires in"} (
                          {value.expiryDateText})
                        </>
                      )}
                    </span>
                  )}
              </div>
            );
          } else {
            // Multiple input controllers (A24, and all DEM)
            // Read the main Intrusion Alarm setting.
            // This value contains the alarm state and its expiration date.
            const intrusionAlarmSummary = getSettingDisplayForController(
              row.original,
              settingType.addr,
              settingType.bit_pos,
              settingType.name,
            );
            return (
              <ExpandablePointSettingCell
                controller={row.original}
                settingType={settingType}
                expanded={expanded}
                onToggle={() =>
                  togglePointCell(row.original, settingType, "Input")
                }
                getPointSettingDisplay={getPointSettingDisplay}
                pointType="Input"
                pointCount={deviceInput}
                summaryValue={intrusionAlarmSummary}
                hasIndefiniteSettings={controllerHasIndefiniteSettings}
              />
            );
          }
        }

        // Show Intrusion Alarm expansion for Air2Four controllers.
        if (shouldUseIntrusionAlarmOutputs(row.original, settingType)) {
          const deviceId = row.original?.id ?? row.original?.device_id;
          const expandKey = `${deviceId}:${settingType.name}:Output`;
          const expanded = !!expandedPointCells[expandKey];

          // Read the main Intrusion Alarm setting independently from the Door Detect outputs.
          const intrusionAlarmSummary = getSettingDisplayForController(
            row.original,
            settingType.addr,
            settingType.bit_pos,
            settingType.name,
          );

          return (
            <ExpandablePointSettingCell
              controller={row.original}
              settingType={settingType}
              expanded={expanded}
              onToggle={() =>
                togglePointCell(row.original, settingType, "Output")
              }
              getPointSettingDisplay={getPointSettingDisplay}
              pointType="Output"
              pointCount={getInputCountForController(row.original)}
              summaryValue={intrusionAlarmSummary}
              hasIndefiniteSettings={controllerHasIndefiniteSettings}
            />
          );
        }

        // Show a single-output dropdown for Air2Mini and Air2One Intrusion Alarm.
        if (shouldUseSingleIntrusionAlarmOutput(row.original, settingType)) {
          const deviceId = row.original?.id ?? row.original?.device_id;
          const expandKey = `${deviceId}:${settingType.name}:Output`;
          const expanded = !!expandedPointCells[expandKey];

          // Read the main Intrusion Alarm setting.
          const intrusionAlarmValue = getSettingDisplayForController(
            row.original,
            settingType.addr,
            settingType.bit_pos,
            settingType.name,
          );

          return (
            <ExpandablePointSettingCell
              controller={row.original}
              settingType={settingType}
              expanded={expanded}
              onToggle={() =>
                togglePointCell(row.original, settingType, "Output")
              }
              getPointSettingDisplay={() => intrusionAlarmValue}
              pointType="Output"
              pointCount={1}
              summaryValue={intrusionAlarmValue}
              hasIndefiniteSettings={controllerHasIndefiniteSettings}
            />
          );
        }

        // Show output expansion for specific features
        if (shouldUseExpandableOutputs(row.original, settingType)) {
          const deviceId = row.original?.id ?? row.original?.device_id;
          const expandKey = `${deviceId}:${settingType.name}:Output`;
          const expanded = !!expandedPointCells[expandKey];

          return (
            <ExpandablePointSettingCell
              controller={row.original}
              settingType={settingType}
              expanded={expanded}
              onToggle={() =>
                togglePointCell(row.original, settingType, "Output")
              }
              getPointSettingDisplay={getPointSettingDisplay}
              pointType="Output"
              pointCount={getOutputCountForController(row.original)}
              hasIndefiniteSettings={controllerHasIndefiniteSettings}
            />
          );
        }

        return (
          <SettingBadge
            value={getValue()}
            showExpiration={settingsWithExpiration.includes(settingType.name)}
            hasIndefiniteSettings={controllerHasIndefiniteSettings}
          />
        );
      },
    }));

    return [...baseColumns, ...settingColumns];
  }, [
    getSettingDisplayForController,
    getPointSettingDisplay,
    getEnhancedSoundSetDisplay,
    expandedPointCells,
    expandedEnhancedCells,

    // Rebuild the status column when refresh results change.
    failedControllerIds,
    failedControllerMessages,
  ]);

  /**
   * Rebuilds the table rows whenever the device settings change.
   * This forces TanStack Table to display the latest refreshed values.
   */
  const tableRows = useMemo(() => {
    return controllerRows.map((controller) => ({
      ...controller,
    }));
  }, [controllerRows, controllerSettingData]);

  /**
   * Creates the TanStack table instance.
   */
  const table = useReactTable({
    data: tableRows,
    columns: settingsColumns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (!isOpen) return null;

  // if (shopControllerLoading || controllerSettingLoading) {
  if (!controllerSettingLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-lg border border-stone-200 p-6 flex justify-center items-center h-64 w-full max-w-md mx-4">
          <OrbitProgress color="#444444" size="medium" />
        </div>
      </div>
    );
  }

  return (
    // body popup
    <div className="fixed inset-0 z-50 flex items-center justify-center ">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* header popup */}
      <div className="relative w-full max-w-[1650px] mx-4 bg-white rounded-xl shadow-lg border border-stone-200">
        <div className="px-5 py-4 rounded-t-xl border-b border-stone-200 bg-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-lg font-semibold text-stone-900 truncate">
                Device Settings
              </div>

              <div className="mt-0.5 text-sm text-stone-600 truncate">
                {shop?.name ?? "Shop"} {shop?.brand ? `- ${shop.brand}` : ""}
              </div>

              {/* <div className="mt-1 flex items-center gap-4 text-sm text-stone-600">
                <div className="text-sm text-stone-600 truncate">
                  Total controllers:{" "}
                  <span className="font-bold p-2 bg-stone-200">
                    {controllers.length}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="px-3 py-1.5 cursor-pointer rounded-lg border border-stone-300 bg-white text-sm font-semibold text-stone-700 hover:bg-stone-50 focus:outline-none focus:ring-2 focus:ring-stone-300"
                >
                  <option value="all">
                    Status Controller ({controllers.length})
                  </option>
                  <option value="online">
                    Online ({onlineControllerCount})
                  </option>
                  <option value="offline">
                    Offline ({offlineControllerCount})
                  </option>
                </select>
              </div> */}

              
{/* Controller Status Summary */}
<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 mx-1">

  {/* Total Controllers */}
  <button
    type="button"
    onClick={() => {
      setStatusFilter("all");
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }}
    className={`cursor-pointer flex items-center justify-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
      statusFilter === "all"
        ? "border-stone-500 bg-stone-200 ring-2 ring-stone-300"
        : "border-stone-200 bg-stone-50 hover:bg-stone-100"
    }`}
  >
    <span className="text-xs font-semibold uppercase text-stone-500">
      Total Controllers
    </span>
    <span className="text-xl font-bold text-stone-900">
      {controllers.length}
    </span>
  </button>

  {/* Online Controllers */}
  <button
    type="button"
    onClick={() => {
      setStatusFilter("online");
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }}
    className={`cursor-pointer flex items-center justify-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
      statusFilter === "online"
        ? "border-emerald-500 bg-emerald-100 ring-2 ring-emerald-200"
        : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
    }`}
  >
    <span className="text-xs font-semibold uppercase text-emerald-700">
      Online
    </span>
    <span className="text-xl font-bold text-emerald-700">
      {onlineControllerCount}
    </span>
  </button>

  {/* Offline Controllers */}
  <button
    type="button"
    onClick={() => {
      setStatusFilter("offline");
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }}
    className={`cursor-pointer flex items-center justify-center gap-3 rounded-lg border px-4 py-3 transition-colors ${
      statusFilter === "offline"
        ? "border-slate-500 bg-slate-200 ring-2 ring-slate-300"
        : "border-slate-200 bg-slate-100 hover:bg-slate-200"
    }`}
  >
    <span className="text-xs font-semibold uppercase text-slate-600">
      Offline
    </span>
    <span className="text-xl font-bold text-slate-700">
      {offlineControllerCount}
    </span>
  </button>

</div>

            </div>

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-semibold"
            >
              Close
            </button>
          </div>
        </div>

        {/* Body div Search and Refresh area */}
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 flex gap-3 justify-between">
          {/* Search Controller Input */}
          <div className="flex w-3xl gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search controllers..."
                className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg"
              />

              {searchTerm.trim() && (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-sm">
                  <span className="invisible whitespace-pre">{searchTerm}</span>
                  <span className="ml-1 font-semibold text-stone-500">
                    ({controllerRows.length})
                  </span>
                </div>
              )}
            </div>

            {/* Clear the current search text. */}
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              disabled={!searchTerm}
              className="px-4 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-sm font-semibold hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>

          {/* Button Refresh */}
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={manualRefreshing}
            className="px-5 py-2 border border-stone-300 rounded-lg bg-stone-100 text-stone-800 font-semibold hover:bg-stone-200 disabled:opacity-50"
          >
            {manualRefreshing ? "Refreshing..." : "Device Refresh"}
          </button>
        </div>

        {/* Summary Buttons */}
        <div className="p-5">
          <div className="flex flex-wrap justify-center gap-2">
            {settingsSummary.slice(0, 10).map((setting) => (
              // body buttons
              <div
                key={setting.name}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-200 shadow-sm"
              >
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 whitespace-nowrap">
                  {settingIcons[setting.name]}
                  {setting.name}
                </span>
                {/* color numbers */}
                <span className="text-xs font-semibold text-stone-500">
                  {setting.onCount}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 pb-4">
          {(shopControllerDataError || controllerSettingError) && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {shopControllerDataError?.message ||
                controllerSettingError?.message ||
                "Error loading device settings."}
            </div>
          )}

          <div className="border border-stone-200 rounded overflow-auto max-h-[50vh]">
            <table className="min-w-full divide-y divide-stone-200">
              <thead className="bg-stone-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-3 py-2 text-left text-xs font-semibold text-stone-700 uppercase bg-stone-50 ${
                          header.column.id === "deviceName"
                            ? "sticky top-0 left-0 z-40 min-w-[200px]"
                            : header.column.id === "status"
                              ? "sticky top-0 left-[200px] z-40 min-w-[140px]"
                              : "sticky top-0 z-30"
                        }`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody className="divide-y divide-stone-200 bg-white">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={settingsColumns.length}
                      className="px-4 py-6 text-center text-sm text-stone-500"
                    >
                      No controllers found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        failedControllerIds.includes(
                          row.original?.id ?? row.original?.device_id,
                        )
                          ? "bg-stone-100 opacity-50 hover:bg-stone-200"
                          : controllerHasInvalidSettings(row.original)
                            ? "bg-orange-50 hover:bg-orange-100"
                            : "hover:bg-stone-50"
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-3 py-2 text-sm text-left align-top ${
                            cell.column.id === "deviceName"
                              ? `sticky left-0 z-20 min-w-[200px] ${
                                  controllerHasInvalidSettings(row.original)
                                    ? "bg-orange-50 hover:bg-orange-100"
                                    : "bg-white"
                                }`
                              : cell.column.id === "status"
                                ? `sticky left-[200px] z-20 min-w-[140px] ${
                                    controllerHasInvalidSettings(row.original)
                                      ? "bg-orange-50 hover:bg-orange-100"
                                      : "bg-white"
                                  }`
                                : ""
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-stone-500">
              Page{" "}
              <span className="font-semibold">{pagination.pageIndex + 1}</span>{" "}
              of <span className="font-semibold">{table.getPageCount()}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-stone-300 disabled:opacity-50"
              >
                Prev
              </button>

              <button
                type="button"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-stone-300 disabled:opacity-50"
              >
                Next
              </button>

              <select
                value={pagination.pageSize}
                onChange={(event) =>
                  setPagination({
                    pageIndex: 0,
                    pageSize: Number(event.target.value),
                  })
                }
                className="px-3 py-2 text-sm border border-stone-200 rounded-lg"
              >
                {[5, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDeviceSettigns;
